import { motion } from "motion/react";
import { Suspense, useRef, type Ref } from "react";

import type { Profile } from "@/api/bale.zod";

import { useGetFriendGroup, useGetFriendGroupSuspense, useRemoveFriendGroupMember } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { CardHeader, CardTitle, MotionCard } from "@/components/Card";
import { IconButton } from "@/components/IconButton";
import { ProfileItem } from "@/components/ProfileItem";
import { Subtitle } from "@/components/Subtitle";
import { TextLoading } from "@/components/TextLoading";

import { AddFriendGroupMemberModal } from "./AddFriendGroupMemberModal";
import { EditFriendGroupModal } from "./EditFriendGroupModal";

export const FriendGroupCard = motion.create(function FriendGroupCard({
  friendGroupId,
  onBack,
  ref,
}: {
  friendGroupId: number;
  onBack: () => void;
  ref: Ref<HTMLDivElement>;
}) {
  const getFriendGroup = useGetFriendGroup(friendGroupId, {
    query: { select: (data) => data.data.friend_group },
  });

  const editFriendGroup = useRef<HTMLDialogElement>(null);
  const addFriendGroupMember = useRef<HTMLDialogElement>(null);

  return (
    <MotionCard ref={ref}>
      <CardHeader>
        <ActionBox className="self-stretch">
          <IconButton type="button" name="chevron-left" onClick={onBack} />
        </ActionBox>
        <CardTitle>
          <Subtitle>{getFriendGroup.data ? getFriendGroup.data.name : <TextLoading />}</Subtitle>
          <p className="font-hint text-placeholder">
            {getFriendGroup.data ? getFriendGroup.data.description : <TextLoading className="w-[60ch]" />}
          </p>
        </CardTitle>
        <ActionBox className="min-h-12 py-2">
          <IconButton type="button" name="edit" onClick={() => editFriendGroup.current?.showModal()} />
          <IconButton type="button" name="user-plus" onClick={() => addFriendGroupMember.current?.showModal()} />
        </ActionBox>
      </CardHeader>
      <Suspense
        fallback={
          <div className="flex flex-col gap-2 px-4 pb-2">
            <TextLoading className="h-12 w-full" />
            <TextLoading className="h-12 w-full" />
            <TextLoading className="h-12 w-full" />
          </div>
        }
      >
        <FriendGroupMembers friendGroupId={friendGroupId} />
        <EditFriendGroupModal
          friendGroupId={friendGroupId}
          ref={editFriendGroup}
          onClose={() => editFriendGroup.current?.close()}
        />
        <AddFriendGroupMemberModal
          friendGroupId={friendGroupId}
          ref={addFriendGroupMember}
          onClose={() => addFriendGroupMember.current?.close()}
        />
      </Suspense>
    </MotionCard>
  );
});

function FriendGroupMembers({ friendGroupId }: { friendGroupId: number }) {
  const friendGroupMembers = useGetFriendGroupSuspense(friendGroupId, {
    query: { select: (data) => data.data.members },
  });
  const removeFromFriendGroup = useRemoveFriendGroupMember();

  return (
    <div className="flex flex-col gap-2 pb-2">
      {friendGroupMembers.data.map((friend) => (
        <FriendGroupMember
          key={friend.profile.id}
          profile={friend.profile}
          onRemove={() => removeFromFriendGroup.mutate({ friendGroupId, data: { friend_id: friend.profile.id } })}
        />
      ))}
    </div>
  );
}

function FriendGroupMember({ profile, onRemove }: { profile: Profile; onRemove: () => void }) {
  return (
    <div className="flex flex-row justify-between gap-4 pr-2 pl-4">
      <ProfileItem profile={profile} />
      <ActionBox>
        <IconButton type="button" name="x" onClick={onRemove} />
      </ActionBox>
    </div>
  );
}
